import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUserProfile } from "@/hooks/use-user-profile";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, Users, ArrowRight, CheckCircle, AlertCircle } from "lucide-react";
import LoadingFallback from "@/components/LoadingFallback";
import { toast } from "sonner";

const WaitingForInvitation: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading, refetch } = useUserProfile();
  const navigate = useNavigate();
  const [invitationToken, setInvitationToken] = useState("");
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState("");
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);

  // Redirect if user already has an organization
  useEffect(() => {
    if (!profileLoading && profile?.organization_id) {
      navigate('/admin');
    }
  }, [profile, profileLoading, navigate]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [authLoading, user, navigate]);

  // Fetch pending invitations for this user
  useEffect(() => {
    const fetchInvitations = async () => {
      if (!user?.email) return;

      const { data, error } = await supabase
        .from('organization_invitations')
        .select(`
          *,
          organizations (
            name
          )
        `)
        .eq('email', user.email)
        .is('used_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (!error && data) {
        setPendingInvitations(data);
      }
    };

    fetchInvitations();
  }, [user]);

  const handleAcceptInvitation = async (token: string) => {
    setIsAccepting(true);
    setError("");

    try {
      const { data, error } = await supabase.rpc('accept_organization_invitation', {
        invitation_token: token
      });

      if (error) throw error;

      toast.success("Successfully joined organization!");
      await refetch();
      navigate('/admin');
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.message || "Failed to accept invitation. Please check the token and try again.");
      toast.error("Failed to accept invitation");
    } finally {
      setIsAccepting(false);
    }
  };

  const handleManualTokenSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (invitationToken.trim()) {
      handleAcceptInvitation(invitationToken.trim());
    }
  };

  if (authLoading || profileLoading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Welcome Card */}
        <Card className="border-2">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Welcome to ZooCertica!</CardTitle>
            <CardDescription className="text-base mt-2">
              Your account has been created successfully. To access the full platform, you'll need to join an organization.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Logged in as:</strong> {user.email}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Your Options:</h3>
              <div className="grid gap-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Create Your Own Organization</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Start fresh with your own facility management setup
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">Wait for an Invitation</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      An organization administrator can invite you via email
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={() => navigate('/organization-setup')} 
              className="w-full"
              size="lg"
            >
              <Users className="w-4 h-4 mr-2" />
              Create Organization
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <Card className="border-primary/50">
            <CardHeader>
              <CardTitle className="text-lg">Pending Invitations</CardTitle>
              <CardDescription>
                You have {pendingInvitations.length} pending invitation{pendingInvitations.length > 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                  <div className="flex-1">
                    <p className="font-medium">{invitation.organizations?.name || 'Organization'}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleAcceptInvitation(invitation.token)}
                    disabled={isAccepting}
                    size="sm"
                  >
                    {isAccepting ? "Accepting..." : "Accept"}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Manual Token Entry */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Have an Invitation Token?</CardTitle>
            <CardDescription>
              Enter your invitation token if you received one separately
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleManualTokenSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="token">Invitation Token</Label>
                <Input
                  id="token"
                  value={invitationToken}
                  onChange={(e) => setInvitationToken(e.target.value)}
                  placeholder="Enter your invitation token"
                  disabled={isAccepting}
                />
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" disabled={!invitationToken.trim() || isAccepting} className="w-full">
                {isAccepting ? "Accepting..." : "Accept Invitation"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Additional Actions */}
        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Need help? Contact us at <a href="mailto:support@zoocertica.com" className="text-primary hover:underline">support@zoocertica.com</a>
          </p>
          <Button variant="ghost" size="sm" onClick={() => navigate('/legal-lookup')}>
            Explore Legal Lookup Tool
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WaitingForInvitation;
